import React, { useCallback, useRef } from "react";
import { useRootDispatch, useRootSelector } from "~redux/hooks";
import { setAsel, viewPositionSelector } from "~redux/slices/edstSlice";
import { trackFromFpIdSelector } from "~/redux/slices/aircraftSlice";
import { openMenuThunk } from "~redux/thunks/openMenuThunk";
import { useDragging } from "hooks/useDragging";
import {
  colorNameMap,
  edstFontName,
  getBitmapTextStyles,
  useCenterCursor,
  useFocused,
  usePixiOnClickOutside,
  useRouteFixes,
} from "@poscon/shared-frontend";
import type { EdstView } from "types/edstView";
import type { Container as PixiContainer } from "pixi.js";
import { isString } from "@poscon/shared-types";
import { MenuTitleBar } from "components/utils/MenuTitleBar";
import { defaultTint, disabledTint, edstFontDim } from "~/utils/constants";
import { layerZIndexMap } from "~/layerZIndexMap";
import { TableField } from "components/utils/TableField";
import { InteractiveContainer } from "../utils/InteractiveContainer";
import { EdstAircraftMenuProps } from "../menus/AircraftMenu";
import { updateEdstFpData } from "~/redux/thunks/processEramMessage";
import { EdstButton } from "../utils/EdstButton";
import { eramHubConnection } from "~/eramHubConnection";

const width = edstFontDim.width * 22;

type ProbeMenuProps = EdstAircraftMenuProps;
export const ProbeMenu = ({ fp, asel }: ProbeMenuProps) => {
  const dispatch = useRootDispatch();
  const track = useRootSelector((state) => trackFromFpIdSelector(state, fp.id));
  const pos = useRootSelector((state) => viewPositionSelector(state, "PROBE_MENU"));
  const ref = useRef<PixiContainer>(null);
  const focused = useFocused(ref);
  useCenterCursor(ref);
  const { startDrag } = useDragging(ref, "PROBE_MENU", "mouseup");

  usePixiOnClickOutside(ref, () => dispatch(setAsel(null)));
  const legs = useRouteFixes(fp, track?.target?.position)?.slice(0, 12);

  const height = (edstFontDim.height + 3) * (5 + (legs?.length ?? 0)) + 6;

  return (
    <InteractiveContainer ref={ref} x={pos.x} y={pos.y} zIndex={layerZIndexMap.prompts} sortableChildren>
      <MenuTitleBar
        title="Stop Probe Menu"
        width={width}
        fontName={edstFontName}
        fill={focused ? colorNameMap.cyan : colorNameMap.mediumGrey}
        withX={false}
        onTitleMouseDown={startDrag}
        view="PROBE_MENU"
      />
      <container y={edstFontDim.height + 3}>
        <graphics
          draw={(graphics) => {
            graphics.clear();
            graphics.rect(0, 0, width, height).fill(0).stroke({ width: 1, color: colorNameMap.grey });
          }}
        />
        <bitmapText
          x={Math.floor(width / 2)}
          y={10}
          text={`${fp.cid} ${fp.callsign}`}
          roundPixels
          anchor={{ x: 0.5, y: 0 }}
          style={{ ...getBitmapTextStyles(edstFontName), fill: defaultTint }}
        />
        <EdstButton
          x={16}
          y={edstFontDim.height * 2 + 4}
          width={width - 32}
          text="Present Position"
          onmousedown={() => {
            eramHubConnection.emit("stopFpProbe", fp.id, { fix: null });
            dispatch(setAsel(null));
          }}
        />
        <container x={8} y={edstFontDim.height * 4 + 4}>
          {legs?.map((leg, i) => (
            <TableField
              key={i}
              textX={2}
              y={(edstFontDim.height + 3) * i}
              width={width - 17}
              text={leg.fix}
              onmousedown={() => {
                eramHubConnection.emit("stopFpProbe", fp.id, { fix: leg.fix });
                dispatch(setAsel(null));
              }}
            />
          ))}
        </container>
        <EdstButton
          x={width - edstFontDim.width * 4 - 15}
          y={height - (edstFontDim.height + 8)}
          text="Exit"
          onmousedown={() => dispatch(setAsel(null))}
        />
      </container>
    </InteractiveContainer>
  );
};
